/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import {
  ConstructionScheduleRestService,
  MaintenanceScheduleRestService,
  PreConstructionScheduleRestService,
  SalesScheduleRestService,
  ScheduleTaskChangeDateAfterCompleteType,
  ScheduleTaskTodoEventType,
  ScheduleTaskType,
  ScheduleType,
  UserRestService,
  UserSystemRole,
} from '@gh/builder-shared-data';
import { EntityInputDialogConfig } from '@gh/common-internal-shared';

import {
  DictionaryService,
  getRouteParam,
  getRouteStaticData,
  InitDialogViewService,
  InitViewService,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FilteredDataQuery, getSlicedData } from '@gh/core-data';
import { Confirmation, DialogRouteSnapshot } from '@gh/core-ui';
import { GridLayoutService } from '@gh/core-ui-grid';
import { map$ } from '@gh/rx/operators';

import { Observable } from 'rxjs';

import {
  filterSystemRolesDictionary,
  ScheduleTaskDetails,
  scheduleTaskDetailsFromDto,
  scheduleTaskDetailsToDto,
  ScheduleTaskNoteListItem,
  ScheduleTaskUserAutocomplete,
  ScheduleTaskUserAutocompleteFilter,
} from './schedule-task-details.model';

type ScheduleRestService = ConstructionScheduleRestService
  | SalesScheduleRestService
  | PreConstructionScheduleRestService
  | MaintenanceScheduleRestService;

@Injectable()
export class ScheduleTaskDetailsBusinessService
  implements InitViewService, InitDialogViewService<EntityInputDialogConfig<ScheduleTaskNoteListItem>> {

  constructor(private gridLayout: GridLayoutService,
              private dictionaryService: DictionaryService,
              private userRestService: UserRestService,
              private constructionRestService: ConstructionScheduleRestService,
              private salesRestService: SalesScheduleRestService,
              private preConstructionRestService: PreConstructionScheduleRestService,
              private maintenanceRestService: MaintenanceScheduleRestService,
  ) {
  }

  initView(route: ActivatedRouteSnapshot): any {
    const { type: scheduleType } = getRouteStaticData(route);
    return {
      systemRoles: this.dictionaryService.getByEnum(UserSystemRole),
      scheduleTaskTodoEventType: this.dictionaryService.getByEnum(ScheduleTaskTodoEventType),
      scheduleTaskTypes: this.dictionaryService.getByEnum(ScheduleTaskType),
      changeDateAfterCompleteTypes: this.dictionaryService.getByEnum(ScheduleTaskChangeDateAfterCompleteType),
      todoGridLayout: this.gridLayout.get('sheduleTaskTodos'),
      noteGridLayout: this.gridLayout.get('sheduleTaskNotes'),
      taskDetails: this.get(
        scheduleType,
        getRouteParam(route, 'scheduleId'),
        getRouteParam(route, 'taskId')),
    };
  }

  initDialogView(route: DialogRouteSnapshot<EntityInputDialogConfig<ScheduleTaskNoteListItem>>): any {
    return route.input;
  }

  initTodoDialogView(): any {
    return {
      systemRoles: this.dictionaryService.getByEnum(UserSystemRole).pipe(map$(filterSystemRolesDictionary(
        ['Franchisee', 'JobAdmin', 'Estimator', 'Supervisor', 'Accountant', 'Salesperson']))),
      salesSystemRoles: this.dictionaryService.getByEnum(UserSystemRole).pipe(map$(filterSystemRolesDictionary(
        ['Franchisee', 'Accountant', 'Salesperson', 'JobAdmin']))),
      todoEventTypes: this.dictionaryService.getByEnum(ScheduleTaskTodoEventType),
    };
  }

  get(type: ScheduleType, scheduleId: ID, taskId: ID): Observable<ScheduleTaskDetails> {
    return this.selectRestService(type).getTask(scheduleId, taskId).pipe(
      map$((details) => scheduleTaskDetailsFromDto(type, details)));
  }

  update(type: ScheduleType,
         scheduleId: ID,
         taskId: ID,
         scheduleTask: ScheduleTaskDetails,
         saveBaseDatesAnswer?: Confirmation): Observable<ScheduleTaskDetails> {
    return this.selectRestService(type)
      .updateTask(scheduleId, taskId, scheduleTaskDetailsToDto(scheduleTask), saveBaseDatesAnswer).pipe(
        map$((details) => scheduleTaskDetailsFromDto(type, details)));
  }

  getAutocompleteUser(query: FilteredDataQuery<ScheduleTaskUserAutocompleteFilter>): Observable<ScheduleTaskUserAutocomplete[]> {
    return this.userRestService.findAllAsDataSource({
      name: query.filter && query.filter.name || '',
      showInactive: false,
      autocomplete: true,
      systemRoles: query.filter && query.filter.systemRoles || void 0,
    }).select(query).pipe(map$(getSlicedData));
  }

  private selectRestService(type: ScheduleType): ScheduleRestService {
    return {
      [ScheduleType.Construction]: this.constructionRestService,
      [ScheduleType.Sales]: this.salesRestService,
      [ScheduleType.PreConstruction]: this.preConstructionRestService,
      [ScheduleType.Maintenance]: this.maintenanceRestService,
    }[type];
  }
}
